import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Shield, Lock, Globe, Mail, AlertTriangle, Database, Bot, Eye } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <SEO 
        title="Privacy Policy | Strider"
        description="Comprehensive privacy policy for Strider. Learn how we collect, process, and protect your personal data in compliance with UK GDPR."
        canonical="/privacy"
      />
      <Header />
      
      <main className="flex-1 pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-brand" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-500">Strider (a trading name of Boldhorn Press LTD)</p>
            </div>
          </div>
          <p className="text-gray-500 mb-8 pb-6 border-b border-gray-200">
            <strong>Last updated:</strong> April 2026 | <strong>Effective date:</strong> April 2026
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Quick Summary Box */}
            <div className="bg-brand/5 border border-brand/20 rounded-xl p-6 not-prose">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-brand" />
                Privacy at a Glance
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>We are fully compliant with UK GDPR and Data Protection Act 2018</span>
                </li>
                <li className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>User data is processed and stored within UK/EU jurisdiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <Bot className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>We use AI to verify workspace data with full transparency</span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>We never sell your personal data to third parties</span>
                </li>
              </ul>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction and Data Controller</h2>
              <p className="text-gray-600">
                Strider ("Strider", "we", "us", or "our") is a trading name of <strong>Boldhorn Press LTD</strong>, 
                a company registered in England and Wales (Company No. 17134876). We are the data controller 
                responsible for your personal data.
              </p>
              <p className="text-gray-600 mt-3">
                This privacy policy explains how we collect, use, store, and protect your information when you 
                use our website at www.strider.work and related services (the "Service"). We are committed to 
                protecting your privacy and handling your data in an open and transparent manner.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Data Protection Officer Contact:</strong><br />
                  Email: <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a><br />
                  Response time: Within 30 days as required by UK GDPR
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="text-gray-600">We collect the following categories of personal data:</p>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 Information You Provide Directly</h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                <li><strong>Contributor Information:</strong> Username, profile picture, and biographical information</li>
                <li><strong>Venue Owner Information:</strong> Business name, contact details, and venue information</li>
                <li><strong>Reviews and Content:</strong> Reviews, ratings, WiFi speed tests, and venue submissions</li>
                <li><strong>Communications:</strong> Information you provide when contacting our support team</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on pages, and navigation paths</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
                <li><strong>Location Data:</strong> Approximate location based on IP address; precise location only with explicit consent</li>
                <li><strong>WiFi Speed Test Data:</strong> Network speed measurements when you voluntarily run tests</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Google Authentication:</strong> Name, email, and profile picture if you sign in with Google</li>
                <li><strong>Public Venue Data:</strong> Business information from public sources to verify venue details</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Legal Basis for Processing</h2>
              <p className="text-gray-600">We process your personal data under the following legal bases as defined by UK GDPR:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-600">
                <li><strong>Contract Performance (Article 6(1)(b)):</strong> Processing necessary to provide our Service to you</li>
                <li><strong>Legitimate Interests (Article 6(1)(f)):</strong> Processing for our legitimate business interests, such as improving our Service, preventing fraud, and marketing (where you would reasonably expect such processing)</li>
                <li><strong>Consent (Article 6(1)(a)):</strong> Where you have given explicit consent, such as for marketing communications or precise location services</li>
                <li><strong>Legal Obligation (Article 6(1)(c)):</strong> Processing necessary to comply with UK law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. AI and Automated Data Processing</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex gap-3">
                <Bot className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">Transparency Notice: AI-Assisted Operations</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Strider employs AI and automated systems to enhance our Service. We believe in full transparency 
                    about how these technologies process your data.
                  </p>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.1 How We Use AI</h3>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Venue Data Verification:</strong> AI assists in verifying venue information accuracy by cross-referencing public sources</li>
                <li><strong>Content Moderation:</strong> Automated screening of reviews for spam, inappropriate content, or policy violations</li>
                <li><strong>WiFi Quality Analysis:</strong> Aggregating and analysing speed test data to provide reliable venue metrics</li>
                <li><strong>Search Optimisation:</strong> Improving search results based on user behaviour patterns</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.2 Human Oversight</h3>
              <p className="text-gray-600">
                All AI-driven decisions that significantly affect users are subject to human review. You have the right 
                to request human intervention in any automated decision-making process that affects you.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.3 Profiling</h3>
              <p className="text-gray-600">
                We do not engage in automated decision-making or profiling that produces legal effects or similarly 
                significant effects on individuals without explicit consent and appropriate safeguards.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Advertising and Third-Party Services</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">5.1 Google AdSense and Advertising</h3>
              <p className="text-gray-600">
                We may display advertisements through Google AdSense and other advertising networks. These services 
                use cookies and similar technologies to serve ads based on your interests. Information collected 
                by advertising partners may include:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>Pages you visit on our site and other sites</li>
                <li>Your interaction with advertisements</li>
                <li>Device identifiers and IP addresses</li>
                <li>Demographic and interest data</li>
              </ul>
              <p className="text-gray-600 mt-3">
                You can opt out of personalised advertising by visiting{" "}
                <a href="https://www.google.com/settings/ads" className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">
                  Google Ads Settings
                </a>{" "}
                or{" "}
                <a href="https://www.aboutads.info/choices/" className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">
                  Digital Advertising Alliance
                </a>.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">5.2 Analytics</h3>
              <p className="text-gray-600">
                We use analytics services to understand how users interact with our Service. This data helps us 
                improve functionality and user experience. Analytics data is aggregated and does not identify 
                individual users.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Information Sharing and Disclosure</h2>
              <p className="text-gray-600">
                <strong>We do not sell your personal information.</strong> We may share your information with:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-600">
                <li><strong>Service Providers:</strong> Third parties that help us operate our Service (hosting, analytics, email services). All service providers are bound by data processing agreements compliant with UK GDPR.</li>
                <li><strong>Advertising Partners:</strong> As described in Section 5.1, for the purpose of serving relevant advertisements.</li>
                <li><strong>Public Content:</strong> Reviews, ratings, and contributions you make are publicly visible on the platform (your profile settings control what identifying information is shown).</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our legal rights.</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets, with appropriate data protection safeguards.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. International Data Transfers</h2>
              <p className="text-gray-600">
                Boldhorn Press LTD is committed to keeping your data within the United Kingdom wherever possible. 
                When data must be transferred outside the UK/EU, we ensure appropriate safeguards are in place:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>Transfers to countries with adequate data protection (as determined by the UK Secretary of State)</li>
                <li>Standard Contractual Clauses approved by the Information Commissioner's Office (ICO)</li>
                <li>Binding Corporate Rules for transfers within corporate groups</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Security</h2>
              <p className="text-gray-600">
                We implement appropriate technical and organisational measures to protect your personal data:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Access controls limiting data access to authorised personnel</li>
                <li>Secure password hashing using industry-standard algorithms</li>
                <li>Regular backups with secure storage</li>
              </ul>
              <p className="text-gray-600 mt-3">
                While we strive to protect your personal data, no method of transmission over the Internet is 100% 
                secure. We cannot guarantee absolute security but will notify you and relevant authorities of any 
                data breach as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Your Rights Under UK GDPR</h2>
              <p className="text-gray-600">Under UK data protection law, you have the following rights:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-600">
                <li><strong>Right of Access (Article 15):</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification (Article 16):</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure (Article 17):</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restriction (Article 18):</strong> Request restriction of processing</li>
                <li><strong>Right to Data Portability (Article 20):</strong> Receive your data in a portable format</li>
                <li><strong>Right to Object (Article 21):</strong> Object to processing, including for direct marketing</li>
                <li><strong>Rights Related to Automated Decision-Making (Article 22):</strong> Not be subject to decisions based solely on automated processing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
              </ul>
              <p className="text-gray-600 mt-3">
                To exercise any of these rights, contact us at{" "}
                <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a>. 
                We will respond within 30 days as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Data Retention</h2>
              <p className="text-gray-600">We retain your personal data according to the following principles:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-600">
                <li><strong>Account Data:</strong> Retained while your account is active, plus 2 years after closure</li>
                <li><strong>Reviews and Contributions:</strong> Retained indefinitely (anonymised if you delete your account)</li>
                <li><strong>Usage Data:</strong> Retained for 26 months, then aggregated/anonymised</li>
                <li><strong>Support Communications:</strong> Retained for 3 years after resolution</li>
                <li><strong>Legal/Compliance Data:</strong> Retained as required by applicable law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Cookies and Tracking</h2>
              <p className="text-gray-600">
                We use cookies and similar technologies as described in our{" "}
                <a href="/cookies" className="text-brand hover:underline">Cookie Policy</a>. You can manage your 
                cookie preferences through your browser settings or our cookie consent tool.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Children's Privacy</h2>
              <p className="text-gray-600">
                Our Service is not intended for individuals under 16 years of age. We do not knowingly collect 
                personal information from children under 16. If you are a parent or guardian and believe your 
                child has provided us with personal information, please contact us at{" "}
                <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new policy on this page and updating the "Last updated" date. For significant 
                changes, we will provide additional notice (such as email notification or a prominent notice 
                on our Service).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Complaints and Regulatory Contact</h2>
              <p className="text-gray-600">
                If you are not satisfied with how we handle your personal data, you have the right to lodge a 
                complaint with the Information Commissioner's Office (ICO):
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Information Commissioner's Office</strong><br />
                  Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />
                  Telephone: 0303 123 1113<br />
                  Website: <a href="https://ico.org.uk" className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
              <p className="text-gray-600">
                For any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Boldhorn Press LTD</strong> (trading as Strider)<br />
                  Company Registration: England & Wales (No. 17134876)<br />
                  <br />
                  Email: <a href="mailto:privacy@strider.work" className="text-brand hover:underline">privacy@strider.work</a><br />
                  General Enquiries: <a href="mailto:hello@strider.work" className="text-brand hover:underline">hello@strider.work</a><br />
                  Website: <a href="https://www.strider.work" className="text-brand hover:underline">www.strider.work</a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
